import { extractPlaceName, haversineDistanceMiles } from 'src/utils/distance.util';

/**
 * NPT-1932 — map search v2: the viewport, the anchor and the URL contract.
 *
 * URL params on the map route:
 *   center=lng,lat   the ANCHOR: the event venue or searched place. Never changed by map moves.
 *   place_name       the anchor's label. Never replaced by a reverse-geocoded map label.
 *   bbox=w,s,e,n     the viewport, written with `replace` on every move so refresh/share restores
 *                    the view without stacking history entries.
 * Everything else (dates, guests, gameId, programSlug, eventSlug, filters) is left untouched by
 * map moves.
 */

export type MapBounds = { west: number; south: number; east: number; north: number };

export type MapAnchor = {
   latitude: number;
   longitude: number;
   /** Short label for distances and the pin, e.g. "Dodger Stadium". */
   label: string;
   /** True for game/event searches: the pin then says "this is where the event is". */
   isEvent: boolean;
};

const round = (n: number, digits = 5) => Math.round(n * 10 ** digits) / 10 ** digits;

/** The four getters of mapbox's LngLatBounds, so this file does not import mapbox-gl. */
type LngLatBoundsLike = { getWest(): number; getSouth(): number; getEast(): number; getNorth(): number };

const wrapLng = (lng: number) => (lng >= -180 && lng <= 180 ? lng : ((((lng + 180) % 360) + 360) % 360) - 180);

/**
 * Mapbox returns unwrapped longitudes after panning across the antimeridian (e.g. west -190), which
 * the API rejects. Wrap into [-180, 180] (west > east then means "crosses the antimeridian", the
 * API's contract) and send the whole world for a view a full turn wide.
 */
export const toBounds = (b: LngLatBoundsLike): MapBounds => {
   const west = b.getWest();
   const east = b.getEast();
   const south = Math.max(-90, b.getSouth());
   const north = Math.min(90, b.getNorth());
   if (east - west >= 360) return { west: -180, south, east: 180, north };
   return { west: wrapLng(west), south, east: wrapLng(east), north };
};

export const formatBbox = (b: MapBounds): string => [b.west, b.south, b.east, b.north].map(n => round(n)).join(',');

export const parseBbox = (raw: string | null | undefined): MapBounds | null => {
   if (!raw) return null;
   const parts = raw.split(',').map(Number);
   if (parts.length !== 4 || parts.some(n => !Number.isFinite(n))) return null;
   const [west, south, east, north] = parts;
   if (south >= north || south < -90 || north > 90 || Math.abs(west) > 180 || Math.abs(east) > 180) return null;
   return { west, south, east, north };
};

export const parseCenter = (raw: string | null | undefined): { latitude: number; longitude: number } | null => {
   if (!raw) return null;
   const [lng, lat] = raw.split(',').map(Number);
   if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) return null;
   return { latitude: lat, longitude: lng };
};

/** The anchor from the map URL, or null when the search has no location. */
export const parseAnchor = (params: Record<string, string | undefined>, fallbackLabel: string): MapAnchor | null => {
   const point = parseCenter(params.center);
   if (!point) return null;
   return {
      ...point,
      label: extractPlaceName(params.place_name) || fallbackLabel,
      isEvent: !!(params.gameId || params.eventSlug),
   };
};

const crossesAntimeridian = (b: MapBounds) => b.west > b.east;

const lngSpan = (b: MapBounds) => (crossesAntimeridian(b) ? 180 - b.west + (b.east + 180) : b.east - b.west);

export const boundsContain = (b: MapBounds, latitude: number, longitude: number): boolean => {
   if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return false;
   if (latitude < b.south || latitude > b.north) return false;
   return crossesAntimeridian(b) ? longitude >= b.west || longitude <= b.east : longitude >= b.west && longitude <= b.east;
};

/**
 * Whether a new viewport is different enough from the last searched one to search again: it
 * moved more than `fraction` of the viewport's width or height, or it zoomed (span changed by
 * more than 15%). Tiny drags and the jitter of a click no longer cost a search.
 */
export const viewportChangedEnough = (prev: MapBounds | null, next: MapBounds, fraction = 0.1): boolean => {
   if (!prev) return true;
   const prevH = prev.north - prev.south;
   const prevW = lngSpan(prev);
   const nextH = next.north - next.south;
   const nextW = lngSpan(next);
   if (prevH <= 0 || prevW <= 0) return true;
   const zoomed = Math.abs(nextH / prevH - 1) > 0.15 || Math.abs(nextW / prevW - 1) > 0.15;
   if (zoomed) return true;
   const dLat = Math.abs((next.north + next.south) / 2 - (prev.north + prev.south) / 2);
   let dLng = Math.abs(next.west + nextW / 2 - (prev.west + prevW / 2));
   if (dLng > 180) dLng = 360 - dLng;
   return dLat > prevH * fraction || dLng > prevW * fraction;
};

export const sameBounds = (a: MapBounds | null, b: MapBounds | null): boolean =>
   !!a && !!b && formatBbox(a) === formatBbox(b);

/** A box around a point, `radiusMiles` on each side. */
export const boundsAround = (latitude: number, longitude: number, radiusMiles: number): MapBounds => {
   const dLat = radiusMiles / 69;
   const dLng = radiusMiles / (69 * Math.max(Math.cos((latitude * Math.PI) / 180), 0.01));
   return { west: longitude - dLng, south: latitude - dLat, east: longitude + dLng, north: latitude + dLat };
};

/** The smallest box containing every point. */
export const boundsOfPoints = (points: Array<{ latitude: number; longitude: number }>): MapBounds | null => {
   const valid = points.filter(p => Number.isFinite(p.latitude) && Number.isFinite(p.longitude));
   if (!valid.length) return null;
   return valid.reduce<MapBounds>(
      (b, p) => ({
         west: Math.min(b.west, p.longitude),
         south: Math.min(b.south, p.latitude),
         east: Math.max(b.east, p.longitude),
         north: Math.max(b.north, p.latitude),
      }),
      { west: Infinity, south: Infinity, east: -Infinity, north: -Infinity },
   );
};

export const boundsCenter = (b: MapBounds) => {
   let lng = b.west + lngSpan(b) / 2;
   if (lng > 180) lng -= 360;
   return { latitude: (b.south + b.north) / 2, longitude: lng };
};

export const milesBetween = (a: { latitude: number; longitude: number }, b: { latitude: number; longitude: number }) =>
   haversineDistanceMiles(a.latitude, a.longitude, b.latitude, b.longitude);

/** URL params that only scope one search location and must not survive a change of place. */
export const LOCATION_SCOPED_PARAMS = ['bbox', 'gameId', 'eventId', 'programSlug', 'eventSlug', 'feature_type', 'location_country', 'poi_category', 'radius', 'fromMap'];

/**
 * Apply a search-bar submit (location, dates, guests) to the map URL.
 *
 * Same place: only dates/guests change, and everything else, including the viewport (`bbox`),
 * the game and the filters, is kept. The map refreshes results for the current view.
 * New place: the location-scoped params are dropped so the map frames the new place.
 */
export const applySearchBarToParams = (
   current: Record<string, string>,
   next: Record<string, string | undefined>,
): Record<string, string> => {
   const samePlace = !!next.center && next.center === current.center;
   const base: Record<string, string> = { ...current };
   // Guest and date params are always re-derived from the submit.
   ['range', 'rooms', 'adults', 'children', 'yearOlds', 'isPet'].forEach(k => delete base[k]);
   if (!samePlace) {
      ['place_name', 'center', 'countryCode', ...LOCATION_SCOPED_PARAMS].forEach(k => delete base[k]);
   }
   const merged: Record<string, string> = { ...base };
   Object.entries(next).forEach(([k, v]) => {
      if (v != null && v !== '') merged[k] = String(v);
   });
   return merged;
};
